import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { StorageService } from '@core/services/util/storage.service';
import { GtmService } from '@core/services/analytics/gtm.service';

import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '@environment';
import OutletKeyConverter from '../services/outlet/outlet-key-converter';
import { DeviceService } from '../services/util/device.service';

@Injectable({
    providedIn: 'root'
})
export class OutletApiService {
    isMobile = false;
    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private gtmService: GtmService,
        private deviceService: DeviceService
    ) {
        this.isMobile = this.deviceService.isMobile();
    }

    private _isPrimaryBrand(brand_id: number): boolean {
        return environment.brand.id === Number(brand_id);
    }

    private _getCatalogUrl(brand_id: number): string {
        const base_url = `${environment.catalogUrl}/catalog`;
        const resource = this._isPrimaryBrand(brand_id) ? '/v4/outlets/apps' : '/v2/outlets/app_brand';
        return base_url + resource;
    }

    private _constructLogParams(source, brandId, errMsg) {
        const lat = this.storageService.get('latitude');
        const lon = this.storageService.get('longitude');
        const currentTime = Date.now();
        this.gtmService.logEvent('js_errors', {
            event_label: source,
            event_value: `${brandId}-${lat}-${lon}-${currentTime}`,
            event_action: errMsg || 'API Failure'
        });
    }

    near(outlet_params: any = {}, brand_id?: number) {
        const brandId = brand_id || environment.brand.id;
        const url = this._getCatalogUrl(brandId);
        const params = this._constructOutletParams(outlet_params, brandId);
        return this.http.get(`${url}`, { params }).pipe(
            map((data: any) => {
                const outlet = new OutletKeyConverter().replaceKey(data.outlet);
                data.outlet = outlet;
                return data;
            }),
            catchError(error => {
                this._constructLogParams('Near API', brand_id, error?.message);
                return throwError(() => error);
            })
        );
    }

    inside(externalParams: any = {}) {
        const params: any = {
            ver: '51',
            platform: 'web',
            brand_id: environment.brand.id
        };

        Object.assign(params, externalParams);
        const url = `${environment.catalogUrl}/catalog/v1/outlets/inside`;

        const agent_id = this.storageService.get('agent_id');
        if (agent_id) {
            params.agent_id = agent_id;
        }

        const custId = this.storageService.get('custId');
        if (custId) {
            params.sp_user_id = custId;
        }

        return this.http.get(url, { params });
    }

    private _constructOutletParams(outletParams: any, brand_id: number) {
        const { lat, lon, open, active, outlet_id, agent_id, service_type } = outletParams;

        let params = new HttpParams()
            .set('lat', lat || this.storageService.get('latitude') || 0)
            .set('lon', lon || this.storageService.get('longitude') || 0)
            .set('ver', '51')
            .set('platform', 'web')
            .set('brand_id', String(brand_id));

        if (agent_id) {
            params = params.append('agent_id', agent_id);
        }

        if (outlet_id) {
            params = params.append('outlet_id', outlet_id);
        }

        if (service_type) {
            params = params.append('service_type', service_type);
        }

        if (!this._isPrimaryBrand(brand_id)) {
            params = params.append('app_id', String(environment.brand.id));
        }

        if (typeof open !== 'undefined') {
            params = params.append('open', open);
        }

        if (typeof active !== 'undefined') {
            params = params.append('active', active);
        }

        if (!this.isMobile) {
            params = params.append('desktop_view', 1);
        }

        return params;
    }
}
